import React, { ReactNode } from 'react'
import { m, AnimatePresence } from 'framer-motion'
import {
  WizardSchema,
  WizardStateSchema,
  WizardFieldSchema,
  WizardFieldProps,
} from '@/lib/definitions/wizard.types'
import { cn } from '@/lib/misc/Utils'
import { isFieldComplete } from '@/lib/hooks/useSchemaWizard'
import CheckIcon from '@icons/check'

export interface WizardSectionProps {
  id?: string
  title?: string
  description?: string
  headerAction?: ReactNode
  headerClassName?: string
  stepNumber?: number
  isComplete?: boolean
  showValidationError?: boolean
  style?: 'plain'
  className?: string
  children: ReactNode
}

export function WizardSection({
  id,
  title,
  description,
  headerAction,
  headerClassName,
  stepNumber,
  isComplete = false,
  showValidationError = false,
  style,
  className,
  children,
}: WizardSectionProps) {
  return (
    <div
      id={id}
      className={cn(
        style === 'plain'
          ? ''
          : 'bg-neutral-20 border border-neutral-30 rounded-2xl transition-opacity duration-300',
        showValidationError && 'ring-4 ring-status-error-400 rounded-2xl',
        className
      )}
    >
      <div className={cn(style === 'plain' ? '' : 'p-1')}>
        {(title || description || headerAction) && (
          <div className={cn('space-y-0.5 py-3 px-3', headerClassName)}>
            <div
              className={cn(
                headerAction ? 'flex justify-between items-center' : ''
              )}
            >
              {title && (
                <h3 className="font-semibold text-neutral-90 whitespace-nowrap overflow-hidden text-ellipsis flex items-center gap-2">
                  {stepNumber !== undefined && (
                    <span className="relative inline-block size-6 shrink-0 align-middle">
                      <AnimatePresence initial={false}>
                        {isComplete ? (
                          <m.span
                            key="check"
                            className="absolute inset-0 inline-flex items-center justify-center rounded-full bg-status-good"
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, opacity: 0, rotate: 90 }}
                            transition={{
                              type: 'spring',
                              stiffness: 700,
                              damping: 22,
                            }}
                          >
                            <CheckIcon className="size-4 text-white [stroke-width:3]" />
                          </m.span>
                        ) : (
                          <m.span
                            key="number"
                            className="absolute inset-0 inline-flex items-center justify-center rounded-full bg-neutral-30 text-neutral-70 text-xs not-uppercase normal-case"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.6, opacity: 0 }}
                            transition={{ duration: 0.12, ease: 'easeOut' }}
                          >
                            {stepNumber}
                          </m.span>
                        )}
                      </AnimatePresence>
                    </span>
                  )}
                  {title}
                </h3>
              )}
              {headerAction}
            </div>
            {description && <p className="text-neutral-50">{description}</p>}
            {showValidationError && (
              <p className="text-status-error-700 text-sm">
                Please complete the required fields in this section.
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

interface SchemaWizardProps<TState extends WizardStateSchema> {
  schema: WizardSchema<TState>
  state: TState
  onChange: (fieldId: string, value: any) => void
  className?: string
  isLoading?: boolean
  sectionProps?: Record<string, Record<string, unknown>>
  validationAttempted?: boolean
  invalidFieldIds?: string[]
  isDisabled?: boolean
}

interface WizardFieldRendererProps<
  TState extends WizardStateSchema,
> extends Omit<WizardFieldProps<TState>, 'fieldId'> {
  field: WizardFieldSchema<TState>
  state: TState
  onChange: (fieldId: string, value: any) => void
  isLoading?: boolean
  sectionProps?: Record<string, Record<string, unknown>>
  stepNumber?: number
  showValidationError?: boolean
  forceDisabled?: boolean
  isComplete?: boolean
  isBlockedByPreviousStep?: boolean
}

function WizardFieldRenderer<TState extends WizardStateSchema>({
  field,
  state,
  onChange,
  isLoading,
  sectionProps = {},
  stepNumber,
  showValidationError = false,
  forceDisabled = false,
  isComplete = false,
  isBlockedByPreviousStep = false,
}: WizardFieldRendererProps<TState>) {
  const FieldComponent = field.component
  const HeaderAction = field.headerAction
  const SidePanel = field.sidePanel
  const fieldProps = sectionProps[field.id] || {}

  let disabledWhenFields: (keyof TState)[] = []
  let isDisabled = false

  if (field.disabledWhen !== undefined && field.disabledWhen !== null) {
    if (typeof field.disabledWhen === 'function') {
      const result = field.disabledWhen(state)
      if (Array.isArray(result)) {
        disabledWhenFields = result
        isDisabled = result.some((fieldName) => !state[fieldName])
      } else {
        isDisabled = !!result
      }
    } else {
      disabledWhenFields = field.disabledWhen
      isDisabled = field.disabledWhen.some((fieldName) => !state[fieldName])
    }
  }

  const isFieldDisabled = isDisabled || forceDisabled || isBlockedByPreviousStep
  const isFieldInert = isFieldDisabled && !field.disabledPlaceholder

  const shouldDim =
    !field.disabledPlaceholder &&
    (isBlockedByPreviousStep ||
      disabledWhenFields.some((fieldName) => !state[fieldName]))

  return (
    <WizardSection
      id={`wizard-field-${field.id}`}
      title={field.title}
      description={field.description}
      headerAction={
        HeaderAction ? (
          <HeaderAction
            fieldId={field.id}
            state={state}
            onChange={onChange}
            isDisabled={isFieldDisabled}
            isLoading={isLoading}
            {...fieldProps}
          />
        ) : undefined
      }
      headerClassName={field.headerClassName}
      stepNumber={stepNumber}
      isComplete={isComplete}
      showValidationError={showValidationError}
      style={field.style === 'plain' ? 'plain' : undefined}
      className={shouldDim ? 'opacity-50' : undefined}
    >
      <div className={cn(SidePanel ? 'flex gap-2 items-stretch' : '')}>
        <div
          className={cn(
            field.style === 'plain'
              ? ''
              : 'p-4 bg-surface rounded-xl shadow-card',
            SidePanel ? 'flex-1 min-w-0' : '',
            isFieldInert ? 'pointer-events-none' : ''
          )}
          inert={isFieldInert || undefined}
        >
          {showValidationError && !field.title && (
            <p className="text-status-error-700 text-sm mb-3">
              Please complete the required fields in this section.
            </p>
          )}
          {isDisabled && field.disabledPlaceholder ? (
            <>{field.disabledPlaceholder}</>
          ) : (
            <FieldComponent
              fieldId={field.id}
              state={state}
              onChange={onChange}
              isDisabled={isFieldDisabled}
              isLoading={isLoading}
              {...fieldProps}
            />
          )}
        </div>
        {SidePanel && (
          <div className="w-56 shrink-0" inert={isFieldInert || undefined}>
            <SidePanel
              fieldId={field.id}
              state={state}
              onChange={onChange}
              isDisabled={isFieldDisabled}
              isLoading={isLoading}
              {...fieldProps}
            />
          </div>
        )}
      </div>
    </WizardSection>
  )
}

export function SchemaWizard<TState extends WizardStateSchema>({
  schema,
  state,
  onChange,
  className = '',
  isLoading = false,
  sectionProps = {},
  validationAttempted = false,
  invalidFieldIds = [],
  isDisabled = false,
}: SchemaWizardProps<TState>) {
  const visibleFields = schema.fields.filter(
    (f) => !f.visibleWhen || f.visibleWhen(state)
  )

  const titledFieldIds = visibleFields
    .filter((f) => f.title && f.style !== 'plain')
    .map((f) => f.id)

  const completionById = new Map(
    visibleFields.map((f) => [f.id, isFieldComplete(f, state)])
  )

  const blockedFieldIds = new Set<string>()
  if (schema.sequential) {
    let hasIncompletePreviousStep = false
    visibleFields.forEach((field) => {
      if (hasIncompletePreviousStep) blockedFieldIds.add(field.id)
      const unlocksNext = field.unlocksNextWhen
        ? field.unlocksNextWhen(state)
        : !field.requiredFields || !!completionById.get(field.id)
      if (!unlocksNext) hasIncompletePreviousStep = true
    })
  }

  return (
    <div
      className={cn(
        'schema-wizard',
        isDisabled && 'opacity-60 select-none transition-opacity duration-300',
        className
      )}
      inert={isDisabled || undefined}
      aria-busy={isDisabled || undefined}
    >
      <div className="space-y-7">
        {visibleFields.map((field) => {
          const stepIdx = titledFieldIds.indexOf(field.id)
          const showValidationError =
            validationAttempted && invalidFieldIds.includes(field.id)
          return (
            <m.div
              key={field.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <WizardFieldRenderer<TState>
                field={field}
                state={state}
                onChange={onChange}
                isLoading={isLoading}
                sectionProps={sectionProps}
                stepNumber={stepIdx >= 0 ? stepIdx + 1 : undefined}
                showValidationError={showValidationError}
                forceDisabled={isDisabled}
                isComplete={completionById.get(field.id)}
                isBlockedByPreviousStep={blockedFieldIds.has(field.id)}
              />
            </m.div>
          )
        })}
      </div>
    </div>
  )
}

export default SchemaWizard
